import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Globe2, MapPin, Plane, Search } from "lucide-react";
import { SEARCH_INDEX, ALL_COUNTRIES, ALL_CITIES } from "@/data/destinations";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = { country: Globe2, city: MapPin, airport: Plane };

export default function SearchBar() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return SEARCH_INDEX.filter((e) => e.keywords.includes(needle)).slice(0, 7);
  }, [q]);

  const go = (entry) => {
    setOpen(false);
    setQ("");
    navigate(entry.path);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="smart-search">
      <div className="flex items-center gap-3 border border-white/15 bg-white/[0.06] px-5 py-4 backdrop-blur-xl transition-colors focus-within:border-[#d4af37]/60">
        <Search size={18} className="shrink-0 text-[#d4af37]" />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          onKeyDown={(e) => { if (e.key === "Enter" && results.length) go(results[0]); }}
          placeholder={t("hero.search")}
          className="w-full bg-transparent text-sm text-white placeholder-white/40 outline-none"
        />
        <span className="hidden whitespace-nowrap font-mono text-[10px] tracking-[0.25em] text-white/35 md:block">{ALL_COUNTRIES.length} · {ALL_CITIES.length} · 5</span>
      </div>

      <AnimatePresence>
        {open && q.trim() && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-x-0 top-full z-30 mt-2 border border-white/10 bg-[#0a1128]/95 backdrop-blur-xl"
            data-testid="search-results"
          >
            {results.length === 0 && (
              <div className="px-5 py-4 text-sm text-white/50" data-testid="search-no-results">
                {t("search.noResults")}
              </div>
            )}
            {results.map((r, i) => {
              const Icon = ICONS[r.type];
              return (
                <button
                  key={`${r.type}-${r.label}-${i}`}
                  data-testid={`search-option-${i}`}
                  onMouseDown={() => go(r)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-white/5"
                >
                  <Icon size={15} className="shrink-0 text-[#d4af37]" />
                  <span className="text-sm text-white">{r.label}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {t(`search.${r.type}`)} · {r.sub}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
